/**
 * WorkWise rows for step 5 (Review & Deploy), rendered as extra reviewSection
 * blocks inside the page's existing reviewGrid. Production also asks for the
 * customer number to be typed again here. Deploying uses the page's own Deploy
 * button and modals. Revision 7: the users of the chosen size and the backend
 * cost estimate per server. A Dedicated DB also shows its SQL image and authentication and a
 * storage summary (workload, data / log / TempDB rows, total disks).
 */
import { ShieldCheck } from 'lucide-react'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'
import { Button } from '@/components/common/Button'
import { toAzureLocation } from '@/config/azureRegions'
import {
  DB_IMAGE_LABEL,
  DB_ROLE_TITLES,
  DEPLOYMENT_MODEL_LABELS,
  ENVIRONMENT_LABELS,
  OS_OPTIONS,
  SQL_AUTH_OPTIONS,
  STORAGE_WORKLOAD_OPTIONS,
  WORKWISE_ADMIN_USERNAME,
  sqlPasswordSecretName,
} from '@/config/workwise'
import type { WorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import { useCostEstimate, useSharedPicks } from '@/hooks/useWorkwise'
import styles from '@/pages/NewDeploymentPage.module.css'
import { useDeploymentWizard } from '@/stores/deploymentWizard'
import type { WorkwiseFormValues } from '@/types/workwise'
import { formatMaxUsers } from '@/utils/maxUsers'
import { describeTopology, vmCount } from '@/utils/workwise/cost'
import { costTotalText } from '@/utils/workwise/costEstimate'
import { dbDiskCount, dbRoleText } from '@/utils/workwise/dbStorage'
import { formNetwork } from '@/utils/workwise/model'
import { APPLICATION_STEP } from '@/utils/workwise/serverErrors'
import { predictResources, previewResourceText } from '@/utils/workwise/terraformPreview'
import { ErrorList, Row, TextField } from './fields'
import { CostEstimateLines, CostEstimateSource } from './WorkwiseCost'
import { NetworkReview } from './WorkwiseNetwork'

const ROLE_LABEL = { app: 'App Server', db: 'Database Server', gateway: 'Gateway Server' } as const

/** What a Dedicated DB is created with: the SQL image, the authentication and the "SQL Server storage". */
export function DbReviewRows({ db }: { db: WorkwiseFormValues['dbServer'] }) {
  const authLabel = SQL_AUTH_OPTIONS.find((o) => o.value === db.sqlAuthentication)?.label ?? db.sqlAuthentication
  const mixed = db.sqlAuthentication === 'sql'
  return (
    <>
      <Row label="SQL Server image" value={DB_IMAGE_LABEL} testId="ww-review-db-image" />
      <Row
        label="SQL authentication"
        value={
          mixed
            ? `${authLabel}, login ${db.sqlLogin.trim()} (password generated, stored in Key Vault as ${sqlPasswordSecretName(db.vmName)})`
            : authLabel
        }
        testId="ww-review-sql-auth"
      />
      <Row
        label="Storage optimized for"
        value={STORAGE_WORKLOAD_OPTIONS.find((o) => o.value === db.storageWorkload)?.label ?? db.storageWorkload}
        testId="ww-review-db-workload"
      />
      {(['data', 'log', 'tempdb'] as const).map((role) => (
        <Row key={role} label={DB_ROLE_TITLES[role]} value={dbRoleText(db.storage, role)} mono testId={`ww-review-db-${role}`} />
      ))}
      <Row label="Database disks" value={dbDiskCount(db.storage)} testId="ww-review-db-disks" />
    </>
  )
}

function ReviewContent({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const values = useWatch({ control }) as WorkwiseFormValues
  const picks = useSharedPicks(values)
  const cost = useCostEstimate(values)
  const { customer, appServer, app, tags, environment, deploymentModel } = values
  const topology = describeTopology(values, picks, flow.sizeInfo)
  const resources = predictResources(values, picks, flow.sizeInfo)
  const network = formNetwork(values, picks)
  const generalErrors = [...(flow.submitError ? [flow.submitError] : []), ...(flow.serverErrors?.general ?? [])]
  const servers = [...topology.created, ...topology.attached].sort(
    (a, b) => ['app', 'db', 'gateway'].indexOf(a.role) - ['app', 'db', 'gateway'].indexOf(b.role),
  )

  return (
    <>
      {generalErrors.length > 0 && (
        <div style={{ gridColumn: '1/-1' }}>
          <ErrorList title="WorkWise deployment was not created:" messages={generalErrors} testId="ww-submit-error" />
        </div>
      )}

      <div className={styles.reviewSection} data-testid="ww-review-customer">
        <h3 className={styles.reviewSectionTitle}>WorkWise Customer</h3>
        <div className={styles.reviewTable}>
          <Row label="Customer" value={`${customer.name} (${customer.number})`} />
          <Row label="Slug" value={customer.slug} mono />
          <Row label="Environment" value={ENVIRONMENT_LABELS[environment] ?? environment} />
          <Row label="Region" value={`${values.region} (${toAzureLocation(values.region)})`} />
          <Row label="CI/CD" value={values.cicd === 'github_actions' ? 'GitHub Actions' : 'Azure DevOps'} />
        </div>
        {environment === 'production' && (
          <div style={{ marginTop: 14 }}>
            <div className={styles.approvalNotice} style={{ marginTop: 0, marginBottom: 12 }}>
              <ShieldCheck size={14} />
              Production: type the customer number to confirm.
            </div>
            <TextField path="customer.confirmNumber" label="Confirm Customer Number" required placeholder={customer.number || '047'} maxLength={3} inputMode="numeric" />
          </div>
        )}
      </div>

      <div className={styles.reviewSection} data-testid="ww-review-servers">
        <h3 className={styles.reviewSectionTitle}>WorkWise Servers</h3>
        <div className={styles.reviewTable}>
          <Row label="Deployment model" value={DEPLOYMENT_MODEL_LABELS[deploymentModel] ?? deploymentModel} testId="ww-review-model" />
          <Row label="Users" value={formatMaxUsers(flow.maxUsersFor(appServer.sku))} testId="ww-review-users" />
          {servers.map((s) => (
            <Row
              key={s.role}
              label={`${ROLE_LABEL[s.role]} · ${s.kind === 'created' ? 'Create' : 'Attach'}`}
              value={`${s.name} (${s.detail})`}
              mono
              testId={`ww-review-${s.role}`}
            />
          ))}
          <Row label="OS" value={OS_OPTIONS.find((o) => o.value === appServer.os)?.label ?? appServer.os} />
          <Row
            label="Admin user"
            value={`${WORKWISE_ADMIN_USERNAME} (password per VM, generated and stored in Key Vault)`}
            testId="ww-review-admin"
          />
          {deploymentModel === 'dedicated' && <DbReviewRows db={values.dbServer} />}
          <Row label="VMs to create" value={vmCount(values)} testId="ww-review-vm-count" />
          <Row label="Est. monthly cost" value={costTotalText(cost)} testId="ww-review-cost" />
        </div>
      </div>

      <div className={styles.reviewSection} data-testid="ww-review-app">
        <h3 className={styles.reviewSectionTitle}>WorkWise Application</h3>
        {values.skipApplication ? (
          <div className={styles.reviewTable}>
            <Row label="Application" value="Skipped: the infrastructure is deployed without it" testId="ww-review-app-skipped" />
            <div style={{ marginTop: 12 }}>
              <Button
                variant="secondary"
                onClick={() => {
                  flow.setSkipApplication(false)
                  useDeploymentWizard.getState().setStep(APPLICATION_STEP)
                }}
              >
                Add application settings
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.reviewTable}>
            <Row label="Version" value={app.version} mono />
            <Row label="Tenant" value={app.tenantName} />
            <Row label="License key" value={app.licenseKeyRef} mono />
            <Row label="DB connection" value={app.dbConnectionRef} mono />
            <Row label="SMTP" value={`${app.smtp.host}:${app.smtp.port}${app.smtp.useTls ? ' (TLS)' : ''}`} mono />
            {app.smtp.username && <Row label="SMTP user" value={app.smtp.username} mono />}
            <Row label="SMTP password" value={app.smtp.passwordRef} mono />
            <Row label="From" value={app.smtp.fromAddress} />
          </div>
        )}
      </div>

      <div className={styles.reviewSection} data-testid="ww-review-tags">
        <h3 className={styles.reviewSectionTitle}>Tags</h3>
        <div className={styles.reviewTable}>
          <Row label="Owner" value={tags.owner} testId="ww-review-owner" />
          {tags.extra.filter((t) => t.key.trim()).map((t) => (
            <Row key={t.key} label={t.key} value={t.value} />
          ))}
        </div>
      </div>

      <NetworkReview network={network} />

      <div className={styles.reviewSection} style={{ gridColumn: '1/-1' }} data-testid="ww-review-cost-section">
        <h3 className={styles.reviewSectionTitle}>WorkWise Cost Estimate (per month)</h3>
        <CostEstimateLines state={cost} testId="ww-review-estimate" />
        <div style={{ marginTop: 12 }}>
          <CostEstimateSource state={cost} testId="ww-review-estimate" />
        </div>
      </div>

      <div className={styles.reviewSection} style={{ gridColumn: '1/-1' }} data-testid="ww-review-resources">
        <h3 className={styles.reviewSectionTitle}>Predicted Resources (client-side, not a Terraform plan)</h3>
        <div className={styles.reviewTable}>
          {resources.map((r, i) => (
            <Row key={`${r.type}-${r.name}-${i}`} label={`${r.action} · ${r.type}`} value={previewResourceText(r)} mono />
          ))}
        </div>
      </div>
    </>
  )
}

function CostSummaryContent() {
  const { control } = useFormContext<WorkwiseFormValues>()
  const values = useWatch({ control }) as WorkwiseFormValues
  const cost = useCostEstimate(values)
  return (
    <div className={styles.costSummary}>
      <span className={styles.costLabel}>Estimated Total</span>
      <span className={styles.costValue} data-testid="ww-review-total">
        {costTotalText(cost, '/month')}
      </span>
    </div>
  )
}

/** The "Estimated Total" box of the review for WorkWise (same markup as the generic one): the backend estimate. */
export function WorkwiseCostSummary({ flow }: { flow: WorkwiseFlow }) {
  return (
    <FormProvider {...flow.form}>
      <CostSummaryContent />
    </FormProvider>
  )
}

/** Extra review sections for WorkWise, placed inside the page's reviewGrid. */
export function WorkwiseReviewSections({ flow }: { flow: WorkwiseFlow }) {
  return (
    <FormProvider {...flow.form}>
      <ReviewContent flow={flow} />
    </FormProvider>
  )
}
